import React from 'react';
import { graphql, Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Layout } from '@components';
import PostCategory from '@components/postCategory';

const serif = `Georgia, 'Iowan Old Style', 'Times New Roman', serif`;

const StyledMainContainer = styled.main`
  .inner {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
  }
`;

const StyledHeader = styled.header`
  text-align: center;

  .blog-title {
    margin: 0;
    color: var(--white);
    font-family: ${serif};
    font-size: clamp(44px, 8vw, 72px);
    font-weight: 600;
    line-height: 1.1;
  }

  .blog-subtitle {
    margin: 20px auto 0;
    max-width: 560px;
    color: var(--light-slate);
    font-size: var(--fz-xl);
    line-height: 1.5;
  }
`;

const StyledHero = styled(Link)`
  display: block;
  width: 100%;
  position: relative;
  margin-top: 60px;
  padding: 60px 50px;
  min-height: 400px;
  border-radius: var(--border-radius);
  overflow: hidden;
  color: var(--lightest-slate);
  text-decoration: none;
  background-color: var(--light-navy);

  @media (max-width: 768px) {
    padding: 32px 24px;
    min-height: 320px;
  }

  &:hover,
  &:focus {
    color: var(--lightest-slate);

    .hero-arrow {
      transform: translateX(4px);
    }
  }

  .hero-bg {
    position: absolute;
    top: 0;
    right: 0;
    width: 60%;
    height: 100%;
    z-index: 0;
    mask-image: linear-gradient(to right, transparent, black 40%);

    .img {
      width: 100%;
      height: 100%;
    }
  }

  .hero-blur {
    position: absolute;
    inset: -40px;
    z-index: 0;
    filter: blur(35px);

    .img {
      width: 100%;
      height: 100%;
    }
  }

  .hero-scrim {
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      90deg,
      rgba(2, 12, 27, 0.65) 0%,
      rgba(2, 12, 27, 0.45) 40%,
      rgba(2, 12, 27, 0) 70%
    );
  }

  .hero-inner {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: calc(50% - 20px);
    min-height: 280px;

    @media (max-width: 768px) {
      width: calc(50% - 12px);
      min-height: 240px;
    }
  }

  .hero-title {
    margin: 0;
    color: var(--white);
    font-family: ${serif};
    font-size: clamp(24px, 3.4vw, 42px);
    font-weight: 600;
    line-height: 1.1;
  }

  .hero-desc {
    margin: 18px 0 0;
    color: var(--light-slate);
    font-size: var(--fz-lg);
    line-height: 1.5;
  }

  .hero-cta {
    display: flex;
    align-items: center;
    margin-top: 35px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .hero-arrow {
    ${({ theme }) => theme.mixins.flexCenter};
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    margin-right: 18px;
    border: 1px solid var(--green);
    border-radius: 50%;
    transition: var(--transition);
  }

  @media (max-width: 600px) {
    --hero-title-size: clamp(28px, 7vw, 36px);
    /* Top padding + category + gap + 70% of the title's first line. */
    --hero-overlap: calc(40px + var(--fz-xs) * 1.4 + var(--hero-title-size) * 0.77);
    padding: 0;
    min-height: 0;

    .hero-bg {
      position: relative;
      width: 100%;
      height: auto;
      aspect-ratio: 3 / 2;
      mask-image: none;

      .img {
        mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, rgba(2, 12, 27, 0) 70%, rgba(2, 12, 27, 0.7) 100%);
        pointer-events: none;
      }
    }

    .hero-scrim {
      display: none;
    }

    .hero-inner {
      width: 100%;
      min-height: 0;
      margin-top: calc(-1 * var(--hero-overlap));
      padding: 28px 24px;
      background: linear-gradient(
        to bottom,
        transparent var(--hero-overlap),
        rgba(2, 12, 27, 0.7) var(--hero-overlap)
      );
    }

    .hero-title {
      font-size: var(--hero-title-size);
    }

    .hero-cta {
      margin-top: 24px;
    }
  }
`;

const StyledPostList = styled.div`
  width: calc(100% - 40px);
  margin: 70px auto 120px;
  border-top: 1px solid var(--lightest-navy);

  @media (max-width: 600px) {
    width: calc(100% - 20px);
  }
`;

const StyledPost = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 260px);
  grid-gap: 40px;
  align-items: center;
  padding: 40px 0;
  border-bottom: 1px solid var(--lightest-navy);

  > * {
    min-width: 0;
  }

  &.no-cover {
    grid-template-columns: 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    grid-gap: 20px;
  }

  &:hover,
  &:focus-within {
    .post-title a {
      color: var(--green);
    }
  }

  .post-title {
    margin: 0;
    font-family: ${serif};
    font-size: clamp(24px, 3vw, 30px);
    font-weight: 600;
    line-height: 1.25;

    a {
      color: var(--lightest-slate);
      text-decoration: none;
      transition: var(--transition);
    }
  }

  .post-desc {
    margin: 12px 0 0;
    color: var(--light-slate);
    font-size: var(--fz-lg);
    line-height: 1.5;
  }

  .post-meta {
    margin: 16px 0 0;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .post-cover {
    border-radius: var(--border-radius);
    overflow: hidden;

    @media (max-width: 600px) {
      max-width: 320px;
    }
  }
`;

const formatDate = date =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const BlogPage = ({ location, data }) => {
  const posts = data.allMarkdownRemark.edges.map(({ node }) => node);
  const hero = posts.find(({ frontmatter }) => frontmatter.featured) || posts[0];
  const rest = posts.filter(node => node !== hero);
  const heroImage = hero && getImage(hero.frontmatter.cover);

  return (
    <Layout location={location}>
      <Helmet title="Blog" />

      <StyledMainContainer>
        <div className="inner">
          <StyledHeader>
            <h1 className="blog-title">Blog</h1>
            <p className="blog-subtitle">This is where I share my thoughts.</p>
          </StyledHeader>

          {hero && (
            <StyledHero to={hero.frontmatter.slug}>
              {heroImage && (
                <>
                  <div className="hero-blur" aria-hidden="true">
                    <GatsbyImage image={heroImage} alt="" className="img" />
                  </div>
                  <div className="hero-bg" aria-hidden="true">
                    <GatsbyImage image={heroImage} alt="" className="img" />
                  </div>
                </>
              )}
              <div className="hero-scrim" />

              <div className="hero-inner">
                {hero.frontmatter.tags?.[0] && (
                  <PostCategory>{hero.frontmatter.tags[0]}</PostCategory>
                )}
                <h2 className="hero-title">{hero.frontmatter.title}</h2>
                {hero.frontmatter.description && (
                  <p className="hero-desc">{hero.frontmatter.description}</p>
                )}
                <span className="hero-cta">
                  <span className="hero-arrow" aria-hidden="true">
                    &rarr;
                  </span>
                  Read the latest
                </span>
              </div>
            </StyledHero>
          )}

          <StyledPostList>
            {rest.map((node, i) => {
              const { title, description, slug, date, cover, tags } = node.frontmatter;
              const image = getImage(cover);

              return (
                <StyledPost key={i} className={image ? '' : 'no-cover'}>
                  <div>
                    {tags?.[0] && <PostCategory>{tags[0]}</PostCategory>}
                    <h3 className="post-title">
                      <Link to={slug}>{title}</Link>
                    </h3>
                    {description && <p className="post-desc">{description}</p>}
                    <p className="post-meta">{formatDate(date)} &nbsp;&bull;&nbsp; Felix Gan</p>
                  </div>

                  {image && (
                    <Link to={slug} className="post-cover" aria-hidden="true" tabIndex={-1}>
                      <GatsbyImage image={image} alt="" />
                    </Link>
                  )}
                </StyledPost>
              );
            })}
          </StyledPostList>
        </div>
      </StyledMainContainer>
    </Layout>
  );
};

BlogPage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default BlogPage;

export const pageQuery = graphql`
  {
    allMarkdownRemark(
      filter: {
        fileAbsolutePath: { regex: "/content/blog/" }
        frontmatter: { draft: { ne: true } }
      }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          frontmatter {
            title
            description
            slug
            date
            tags
            featured
            cover {
              childImageSharp {
                gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH, formats: [AUTO, WEBP, AVIF])
              }
            }
          }
        }
      }
    }
  }
`;
